import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Fab, Zoom } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-v7';
import { AlertFilter, DictLockerStatus, DictPadLockMode } from 'shared';
import { filterData, useAlertsData, useScrollToHash } from './alerts.logic';
import { StyledPaperContainer, StyledHeadSection } from './StyledComponents';
import { observer } from 'mobx-react-lite';
import { Filters, ActivePreset } from './filters/Filters';
import { ItemListGroup } from './ItemListGroup';
import { ILockerGateItem } from 'api/interfaces/lockerGate';
import { useScrollToTop } from 'shared/hooks/useScrollToTop';
import { alertStore } from 'stores/alertStore/alertStore';
import { locationStore } from 'stores/locationStore/locationStore';

export const ALL_FILTERS = new Set([
	AlertFilter.Opened,
	AlertFilter.Unpaid,
	AlertFilter.Rented,
	AlertFilter.PendingVerification,
	AlertFilter.Available,
	AlertFilter.OutOfUse,
	AlertFilter.Padlock,
	AlertFilter.Gates,
]);

const ON_SITE_ACTION_FILTERS = new Set([AlertFilter.PendingVerification, AlertFilter.Padlock]);

export const Alerts = observer(() => {
	const { t } = useTranslation();
	const { locationId } = useParams();
	const [activeFilters, setActiveFilters] = useState<Set<AlertFilter>>(ALL_FILTERS);
	const [activePreset, setActivePreset] = useState<ActivePreset>('all');
	const [isRefreshing, setIsRefreshing] = useState(false);
	const { fetchedAlerts: alerts, isLoading } = useAlertsData();
	const { showScrollTop, handleScrollTop } = useScrollToTop();

	useScrollToHash(!!alerts);

	const filteredAlerts = useMemo(() => {
		return alerts?.filter((x) => filterData(x, alertStore.searchPhrase, activeFilters));
	}, [alerts, alertStore.searchPhrase, activeFilters]);

	const handleFilterClick = (filter: AlertFilter) => {
		// First click after a preset selects only this filter and clears the preset;
		// every following click adds/removes (multi-select).
		if (activePreset !== null) {
			setActiveFilters(new Set([filter]));
			setActivePreset(null);
			return;
		}
		setActiveFilters((prev) => {
			const next = new Set(prev);
			next.has(filter) ? next.delete(filter) : next.add(filter);
			return next;
		});
	};

	const handleApplyAll = () => {
		setActiveFilters(new Set(ALL_FILTERS));
		setActivePreset('all');
	};
	const handleApplyOnSiteActions = () => {
		setActiveFilters(new Set(ON_SITE_ACTION_FILTERS));
		setActivePreset('onSite');
	};

	const handleRefresh = async () => {
		if (!locationId || isRefreshing) return;
		setIsRefreshing(true);
		await alertStore.fetchAlerts(locationId);
		setIsRefreshing(false);
	}

	const itemListGroups = useMemo(() => {
		const typeOrder = ['#lockers', '#parkings', '#offices', '#services'];
		const capitalizeFirst = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
		const typePluralLabel = (typeKey: string) =>
			t(`common.lockerTypePlural_${typeKey}`, { defaultValue: '' }) || t('common.lockerTypePlural_#lockers');

		const statusGroups = [
			{
				template: 'pages.locations.groupReservation',
				filter: (x: ILockerGateItem) =>
					(x.isLocker && x.lockerStatus === DictLockerStatus.Reservation) ||
					x.lockerStatus === DictLockerStatus.Reserved,
			},
			{
				template: 'pages.locations.groupPendingVerification',
				filter: (x: ILockerGateItem) => x.isLocker && x.lockerStatus === DictLockerStatus.PendingVerification,
			},
			{
				template: 'pages.locations.groupRented',
				filter: (x: ILockerGateItem) =>
					(x.isLocker && x.lockerStatus === DictLockerStatus.Rented) ||
					x.lockerStatus === DictLockerStatus.Resigned,
			},
			{
				template: 'pages.locations.groupOutOfUse',
				filter: (x: ILockerGateItem) => x.isLocker && x.lockerStatus === DictLockerStatus.OutOfUse,
			},
			{
				template: 'pages.locations.groupAvailable',
				filter: (x: ILockerGateItem) => x.isLocker && x.lockerStatus === DictLockerStatus.Available,
			},
		];

		const groups: { label: string; items: ILockerGateItem[] }[] = [
			{
				label: t('pages.locations.gates'),
				items: filteredAlerts?.filter((x) => !x.isLocker) || [],
			},
		];

		statusGroups.forEach((statusGroup) => {
			const items = filteredAlerts?.filter(statusGroup.filter) || [];

			// No data yet (e.g. still loading) — keep a single placeholder so the skeleton shows.
			if (items.length === 0) {
				groups.push({
					label: capitalizeFirst(t(statusGroup.template, { type: typePluralLabel('#lockers') })),
					items: [],
				});
				return;
			}

			const itemsByType = new Map<string, ILockerGateItem[]>();
			items.forEach((item) => {
				const typeKey = item.lockerDetails?.orderItemTypeTag || '#lockers';
				const bucket = itemsByType.get(typeKey);
				if (bucket) bucket.push(item);
				else itemsByType.set(typeKey, [item]);
			});

			const orderedTypeKeys = [
				...typeOrder.filter((key) => itemsByType.has(key)),
				...[...itemsByType.keys()].filter((key) => !typeOrder.includes(key)),
			];

			orderedTypeKeys.forEach((typeKey) => {
				groups.push({
					label: capitalizeFirst(t(statusGroup.template, { type: typePluralLabel(typeKey) })),
					items: itemsByType.get(typeKey) || [],
				});
			});
		});

		return groups;
	}, [t, filteredAlerts]);

	return (
		<Box mb={12}>
			<StyledPaperContainer>
				<StyledHeadSection>
					<Filters
						activeFilters={activeFilters}
						onFilterClick={handleFilterClick}
						onApplyAll={handleApplyAll}
						onApplyOnSiteActions={handleApplyOnSiteActions}
						activePreset={activePreset}
						alerts={alerts}
						padlockEnabled={
							Boolean(locationStore.selectedLocation?.padLockEnabled) &&
							locationStore.selectedLocation?.dictPadLockMode !== DictPadLockMode.Permanent
						}
					/>
				</StyledHeadSection>
			</StyledPaperContainer>

			{itemListGroups.map((group) => (
				<ItemListGroup key={group.label} label={group.label} items={group.items} isLoading={isLoading} />
			))}

			<Fab
				color='secondary'
				onClick={handleRefresh}
				size='medium'
				disabled={isRefreshing}
				sx={{
					position: 'fixed',
					bottom: (theme) => theme.spacing(4),
					right: (theme) => theme.spacing(showScrollTop ? 12 : 4),
					transition: 'right 0.4s ease-in-out',
					transitionDelay: showScrollTop ? '0s' : '0.2ms',
				}}
			>
				<SyncIcon
					sx={{
						...(isRefreshing && {
							animation: 'spin 1s linear infinite',
							'@keyframes spin': {
								'0%': { transform: 'rotate(0deg)' },
								'100%': { transform: 'rotate(-360deg)' },
							},
						}),
					}}
				/>
			</Fab>

			<Zoom in={showScrollTop} style={{ transitionDelay: showScrollTop ? '0.2s' : '0s' }}>
				<Fab
					color='primary'
					onClick={handleScrollTop}
					size='medium'
					sx={{
						position: 'fixed',
						bottom: (theme) => theme.spacing(4),
						right: (theme) => theme.spacing(4),
						transitionDelay: '0.3s',
					}}
				>
					<KeyboardArrowUpIcon />
				</Fab>
			</Zoom>
		</Box>
	);
});
